import dayjs from 'dayjs';
import { action, computed, observable, set } from 'mobx';
import { TDefaultAutoExpirationOption } from '~/apps/main/CompanySettings/CompanyPreferences/server-types';
import { isFeatureActive } from '~/common/user.utils';
import { TFeature } from '~/components/FeatureRestriction/types/feature-restriction.types';
import { TIntentType } from '~/components/IntentHelpers/intent.utils';
import { IOrganization } from '~/components/OrganizationView/types/organization-view.types';
import { MSP_FEATURE_DATE } from '~/components/Signup/constants/general';
import { UtmCampaigns } from '~/constants/utm-campaigns';
import { UtmSources } from '~/constants/utm-sources';
import { MinimalCompany } from '~/models/company.model';
import type { AuthenticatedGlobalContextValue } from '~/providers/base-global-context/authenticated-global.context';
import { RolloutFeatureFlags } from '~/services/feature.service';
import {
    AtwsStages,
    HbUserType,
    HbUserTypes,
    UserIntentType,
    UserIntentTypes,
    UserTypeDisplayName,
    WorkspaceRoles
} from '~/types/enums.types';
import { intentToKey } from '~/types/import-pdf.types';
import {
    IABTests,
    IAttribution,
    IAuditeeCompany,
    IEmailSignature,
    IImage,
    PricingPlan,
    SystemUserType,
    TABTestKeys,
    TABTestValue,
    UserStatus
} from '~/types/model.types';
import {
    IAngularUser,
    ICalendarColors,
    IGoogleIntegration,
    IHappyMoments,
    IMember,
    IOnboardingContainer,
    IPaymentMethod,
    IUserPermissions,
    SocialNetworkAccount,
    TeamRole,
    TeamRoleToCd,
    IStripeBankAccount,
    IWorkspaceUserDataResponse,
    IUserResponse,
    IImportTemplateResult,
    IOnboardingStep
} from '~/types/server.types';
import { featureVariationsToFeatureRestrictionMap } from '~/utils/account.utils';
import { PaymentMethod } from '~/models/payment-method.model';
import { UserController } from '~/controllers/user.controller';
import { UserService } from '~/services/user.service';
import { tryParseToJsonString } from '~/utils/json.utils';
import { TPartner } from '~/apps/main/Partners/partners.types';
import { OnboardingSequence } from '~/models/onboarding-sequence.model';
import type { IEvent } from '~/types/model.types';
import type { IWorkspaceMember } from '~/types/angular.types';

export interface User extends IMember {}

export type IFlowsBetaUserType = User['flowsBetaUserType'];

export class User {
    globalContext: AuthenticatedGlobalContextValue;

    _id: string;
    full_name: string;
    email: string;
    phone_number?: string;
    api_version: string;
    hb_user_type?: HbUserType[];
    authentication_token: string;
    is_temp_pass_login: boolean;
    es_version: string;
    @observable email_signatures: IEmailSignature[];
    @observable company_id: string;
    @observable default_company_id: string;
    @observable default_flow_signature: {
        signature: string;
        signature_type: string;
    };
    onboarding_container: Partial<IOnboardingContainer>;
    user_company_permission?: IUserPermissions;
    features_variations?: Partial<Record<RolloutFeatureFlags, string>>;
    did_signup_on_mobile_app?: boolean;
    features_active?: string[];
    initials?: string;
    organization?: IOrganization;
    @observable profile_image?: IImage;
    system_user_type?: SystemUserType;
    is_client_user_type?: boolean;
    recurly_key?: string;
    job_title?: string;
    first_login?: string;
    first_time_on_chrome_extension?: string;
    is_real_chargeable_user?: boolean;
    is_password_exposed?: boolean;
    is_email_deliverable?: boolean;
    is_company_owner?: boolean;
    @observable is_account_owner?: boolean;
    @observable attribution: IAttribution;
    workspace_user_data?: IWorkspaceUserDataResponse;
    @observable is_activated: boolean;
    is_active?: boolean;
    referral_container?: {
        referred_by_code: string;
        pending_referral_code: string;
    };
    user_intent: TIntentType;
    is_fullstory_record_enabled?: boolean;
    user_type_name?: UserTypeDisplayName;
    activated_on?: string;
    is_hb_admin?: boolean;
    imported_template_result?: IImportTemplateResult;
    admin_token?: string;
    bucket_number?: number;
    profile_image_url?: string;
    created_at?: string;
    website_url?: string;
    last_file_sent_time?: string;
    @observable has_old_calendly_integration?: boolean;
    @observable calendly_url?: string;
    @observable calendly_email?: string;
    @observable zapier_key?: string;
    @observable account_id?: string;
    @observable plaid_env: string;
    @observable plaid_public_key: string;
    @observable stripe_key: string;
    @observable gift_card_feature_allowed?: boolean;
    @observable disable_client_pvl_requests?: boolean;
    @observable ab_tests: Partial<IABTests> = {};
    @observable user_ui_persistences: {
        key: string;
        value: Record<string, any>;
    }[]; // Don't access this directly, use UiPersistenceStore instead
    @observable first_time_sent_flow_on: string;
    @observable first_time_sent_flow_test_on: string;
    @observable is_pro: boolean;
    @observable is_educator: boolean;
    @observable intercom_hmac?: string;
    @observable is_hb_funds_enabled: boolean;
    // these are only the last 4 digits of the phone number
    @observable phone_number_for_two_factor_auth?: string;
    statsig_stable_id?: string;
    @observable notes?: string;
    @observable contact?: IWorkspaceMember;
    @observable email_verified?: boolean;
    @observable google_integrations?: IGoogleIntegration[];
    @observable bookkeeperModeCompanyId?: string;
    @observable audited_companies?: IAuditeeCompany[];
    @observable login_2fa_enabled?: boolean;
    @observable companies?: MinimalCompany[];
    @observable has_network_user?: boolean;
    @observable is_pro_login?: boolean;
    @observable is_bookkeeper?: boolean;
    @observable isNewNavbarExperience?: boolean;
    @observable payment_methods?: PaymentMethod[];
    @observable enabled_features?: Array<any>;
    @observable workspaces_count: number;
    @observable social_network_accounts?: SocialNetworkAccount[];
    @observable ooo_in_effect?: boolean;
    has_password?: boolean;
    last_login_time: string;
    @observable nylas_calendar_feature_allowed?: boolean;
    @observable password?: string;
    @observable pending_happy_moments?: IHappyMoments;
    @observable curr_device_token_pending_2fa?: boolean;

    // #region TODO move to a dedicated pdf store
    @observable isFlowFromPDFReady?: boolean = false;
    @observable shouldShowPDFPopupWhenReady?: boolean = false;
    @observable pdfFlowId?: string;
    @observable isProcessingPDF?: boolean = false;
    @observable isCreatingTemplate?: boolean = false;
    @observable currTimeoutRef?: NodeJS.Timeout;
    @observable storedWorkspaceProps?:
        | { workspace_id: string }
        | { event: IEvent };
    @observable pdfRequestId?: string;
    @observable gmail_integration_revoked_at?: string;
    @observable has_canva_integration?: boolean;
    @observable has_logged_in_via_desktop?: boolean;
    @observable
    default_auto_expiration_period?: TDefaultAutoExpirationOption | null;
    @observable leads_capture_config?: {
        enabled: boolean;
        is_eligible: boolean;
    };
    calendar_colors?: ICalendarColors;
    hb_location?: IAngularUser['hb_location'];
    atws_status?: IAngularUser['atws_status'];
    @observable is_mobile_active?: boolean;
    // #endregion

    @observable finBotAuthToken?: string;
    @observable userSetupSequence?: OnboardingSequence;

    constructor(
        user: IAngularUser,
        globalContext: AuthenticatedGlobalContextValue
    ) {
        this.globalContext = globalContext;

        // destructure associations and "normalize" data
        const { company, account, bookkeeperModeCompany, ...rest } = user;

        if (account) {
            this.account_id = account._id;
        }

        if (company) {
            this.company_id = company._id;
        }

        if (bookkeeperModeCompany) {
            this.setBookkeeperModeCompanyId(bookkeeperModeCompany._id);
        }

        set(this, rest);
    }

    @action
    update(fields: Partial<User>) {
        set(this, fields);
    }

    @action.bound
    setCalendlyUrl(url: string | null) {
        this.calendly_url = url;
    }

    @action.bound
    setIsNewNavbarExperience(state: boolean) {
        this.isNewNavbarExperience = state;
    }

    @action.bound
    setUserIntent(intent?: TIntentType) {
        this.user_intent = intent;
    }

    get pdfUserIntent() {
        return this.user_intent as keyof typeof intentToKey;
    }

    // #region TODO move to a dedicated pdf store
    @action.bound
    setIsFlowFromPDFReady(state: boolean) {
        this.isFlowFromPDFReady = state;
    }

    @action.bound
    setShouldShowPDFPopupWhenReady(state: boolean) {
        this.shouldShowPDFPopupWhenReady = state;
    }

    @action.bound
    setStoredWorkspaceProps(
        workspaceProps: { workspace_id: string } | { event: IEvent }
    ) {
        this.storedWorkspaceProps = workspaceProps;
    }

    @action.bound
    setIsProcessingPDF(state: boolean) {
        this.isProcessingPDF = state;
    }

    @action.bound
    setIsCreatingTemplate(state: boolean) {
        this.isCreatingTemplate = state;
    }

    @action.bound
    setCurrTimeoutRef(ref: NodeJS.Timeout) {
        this.currTimeoutRef = ref;
    }

    @action.bound
    setPdfFlowId(flowId: string) {
        this.pdfFlowId = flowId;
    }

    @action.bound
    setDisableClientPvlRequests(state: boolean) {
        this.disable_client_pvl_requests = state;
    }

    @action.bound
    setPdfRequestId(requestId: string) {
        this.pdfRequestId = requestId;
    }

    @action.bound
    resetPdfFlowState() {
        this.setIsFlowFromPDFReady(false);
        this.setShouldShowPDFPopupWhenReady(false);
        this.setStoredWorkspaceProps(null);
        this.setIsProcessingPDF(false);
        this.setIsCreatingTemplate(false);
        this.setCurrTimeoutRef(null);
        this.setPdfRequestId(null);
    }

    // #endregion

    @action
    setAccountId(id: string) {
        this.account_id = id;
    }

    @action
    clearPendingReferralContainer() {
        if (this.referral_container) {
            this.referral_container.referred_by_code = null;
            this.referral_container.pending_referral_code = null;
        }
    }

    @action
    setPendingHappyMoments(happyMoments: IHappyMoments) {
        this.pending_happy_moments = happyMoments;
    }

    @computed
    get isMalkutUser() {
        return this.hb_user_type?.includes(HbUserTypes.MALKUT);
    }

    @computed
    get nTillTrialEnds() {
        return this.account?.trial?.n_to_end;
    }

    getTrial() {
        return this.account?.trial;
    }
    @computed
    get shouldStartTrial() {
        return this.isVendor() && this.getTrial() == null && !this.isSubscribed;
    }

    shouldSeeTrialWarnings() {
        const participatesInTrial =
            Object.keys(this.getTrial() || {}).length > 0;
        const subscribed = this.companyHasSubscription;
        const inTrialOrTrialExpired = this.hasTrialExpired || this.isInTrial;

        return participatesInTrial && inTrialOrTrialExpired && !subscribed;
    }

    @computed
    get isAtwsVendor() {
        return (
            this.isVendor() &&
            this.attribution?.user_source_secondary === 'workspace_member'
        );
    }

    @computed
    get isAtwsPreTrialVendor() {
        return this.isAtwsVendor && this.shouldStartTrial;
    }

    @computed
    get isAtwsTrialVendor() {
        return this.isAtwsVendor && this.getTrial() != null && this.isTrialer;
    }

    getAtwsStage() {
        if (this.isClient()) {
            return null;
        }

        if (
            this.atws_status?.onboarding_created === null &&
            this.atws_status?.trial_created === null
        ) {
            return AtwsStages.beforeCreatingFirstEvent;
        } else if (
            this.atws_status?.onboarding_created !== null &&
            this.atws_status?.trial_created === null
        ) {
            return AtwsStages.createdFirstEvent;
        } else if (
            this.atws_status?.onboarding_created !== null &&
            this.atws_status?.trial_created !== null
        ) {
            return AtwsStages.createdSecondEvent;
        }
    }

    // #region Associations
    @computed
    get account() {
        return this.globalContext.accountStore.byId(this.account_id);
    }

    @computed
    get company() {
        return this.globalContext.companyStore.byId(this.company_id);
    }

    @computed
    get hasFinishAuthenticating() {
        return (
            (this.system_user_type || this.hb_user_type) &&
            !this.curr_device_token_pending_2fa
        );
    }

    // #endregion Associations
    /*
THIS IS STALE DATA!!ֿ
READ COMMENT BELOW
*/
    @action.bound
    isGoogleIntegrationActive(integrationType: string) {
        for (const integration of this.google_integrations || []) {
            for (const permission of integration.google_integration_permissions ||
                []) {
                if (
                    permission.integration_type === integrationType &&
                    permission.status === 'active'
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    /*
THIS IS STALE DATA!!
When a user logs in the app, we fetch user data from `api/v2/users/tokens`.
When a logged-in user re-enters the app, we fetch user data from `api/v2/users/:user_id`.
the 2nd endpoint does not include the Google integration data.

Even if the backend were updated to include Google integration details, there is a timing issue:
- After a Google integration is connected, the fetch from `api/v2/users/:user_id`
will not include the new integration data. this is because we redirect to app before the data is updated in the backend.

To ensure you have the most up-to-date data:
Use `userService.getEmailProvider` and extract `data.selected_provider`.
*/
    @computed
    get activeGoogleIntegrations() {
        const activeIntegrations: Record<string, boolean> = {};

        if (Array.isArray(this.google_integrations)) {
            for (const integration of this.google_integrations) {
                if (Array.isArray(integration.google_integration_permissions)) {
                    for (const permission of integration.google_integration_permissions) {
                        if (permission.status === 'active') {
                            activeIntegrations[permission.integration_type] =
                                true;
                        }
                    }
                }
            }
        }

        return activeIntegrations;
    }

    @computed
    get isSubscriptionPaused() {
        const subscription = this.account?.subscription;
        return (
            subscription?.paused_start_date &&
            (subscription.state === 'active' || subscription.state === 'paused')
        );
    }

    @computed
    get isSubscriptionCanceled() {
        return (
            this.account?.subscription?.state === 'canceled' ||
            !!this.account?.subscription?.pricing_plan?.canceled
        );
    }

    isAppSubscription() {
        if (
            !this.account?.subscription?.pricing_plan ||
            this.isSubscriptionPaused
        ) {
            return false;
        }
        return !!this.account?.subscription?.pricing_plan
            ?.in_app_purchase_device;
    }

    @computed
    get storeWrapper() {
        if (!this.isAppSubscription()) {
            return 'BillingModalWrapper';
        }
        if (
            this.account?.subscription.pricing_plan?.in_app_purchase_device ===
            'android'
        ) {
            return 'GooglePlayStoreModalWrapper';
        } else {
            return 'AppStoreModalWrapper';
        }
    }

    isActivated() {
        return !!this.is_activated;
    }

    isActive() {
        return !!this.is_active;
    }

    isHoneypaid() {
        return this.activated_on;
    }

    getTrialStartDate() {
        return this.account?.trial?.start_date ?? null;
    }

    isHbUserType(type: HbUserType) {
        return (
            Array.isArray(this.hb_user_type) && this.hb_user_type.includes(type)
        );
    }

    isVendor() {
        return this.system_user_type === 'vendor';
    }

    isBookkeeper() {
        return this.isHbUserType(HbUserTypes.BOOKKEEPER) || this.is_bookkeeper;
    }

    isDemo() {
        return this.isHbUserType(HbUserTypes.DEMO);
    }

    get isPendingSetupTwoFactorAuth() {
        return this.login_2fa_enabled && !this.phone_number_for_two_factor_auth;
    }

    hasMultipleCompanies() {
        return this.companies?.length > 1;
    }

    isPartOfCompany(companyId: string) {
        return this.companies?.some(company => company._id === companyId);
    }

    getUserStatus() {
        if (this.isSubscriptionCanceled) {
            if (this.isHoneypaid()) {
                return UserStatus.ActivatedSubChurn;
            }
            return UserStatus.SubCancel;
        }
        if (this.companyHasSubscription) {
            if (this.isActivated()) {
                return UserStatus.Activated;
            }
            return UserStatus.Subscribed;
        }
        if (this.isHoneypaid()) {
            return UserStatus.Honeypaid;
        }
        if (this.isInTrial) {
            return UserStatus.Trial;
        }
        if (this.hasTrialExpired) {
            return UserStatus.TrialExpired;
        }
        return UserStatus.PreTrailer;
    }

    @computed
    get isInTrial() {
        const trial = this.account?.trial;
        return trial && !trial.ended;
    }

    @computed
    get isInBookkeeperMode() {
        return !!this.bookkeeperModeCompanyId;
    }

    @computed
    get hasTrialExpired() {
        return this.account?.trial?.ended;
    }

    @computed
    get isSubscribed() {
        return this.account?.is_subscribed;
    }

    @computed
    get isTrialer() {
        return !this.isSubscribed;
    }

    get hasSubscriptions() {
        return this.account?.has_subscriptions;
    }

    @computed
    get utmCampaign() {
        return this.attribution?.user_utm_params?.utm_campaign;
    }

    @computed
    get utmSource() {
        return this.attribution?.user_utm_params?.utm_source;
    }

    @computed
    get utmCustomer() {
        return this.attribution?.user_utm_params?.utm_customer;
    }

    @computed
    get partnershipSource(): TPartner {
        return this.attribution?.user_utm_params?.partnership;
    }

    @computed
    get isPrismmUser() {
        return this.utmSource === UtmSources.Prismm;
    }

    @computed
    get isPrismmUserBeforeIntegration() {
        return this.isPrismmUser && !this.company.has_prismm_integration;
    }

    @computed
    get shouldShowCommunityLink() {
        return (
            (this.isSubscribed &&
                !this.isInBookkeeperMode &&
                this.isVendor()) ||
            this.utmCampaign === UtmCampaigns.SoloCourseV1
        );
    }

    @computed
    get notSelectedNextPlanCode() {
        return (
            this.isStarterPlan &&
            (this.account.selected_next_plan_code === null ||
                this.account.selected_next_plan_code === 'disable')
        );
    }

    @computed
    get isStarterPlan() {
        return (
            this.account?.subscription &&
            this.account.subscription.plan_code === PricingPlan.StarterMonthly
        );
    }

    @computed
    get isLegacyStarterPlan() {
        const plan_type = this.account?.subscription?.pricing_plan?.plan_type;
        return plan_type === 'starter_monthly';
    }

    @computed
    get planType() {
        return this.account?.subscription?.pricing_plan?.plan_type;
    }

    @computed
    get isMembershipFree() {
        return this.account?.subscription?.pricing_plan?.original_amount === 0;
    }

    @computed
    get hasAnyActiveSubscription() {
        return this.isMembershipFree || this.isSubscribed;
    }

    isFeatureRestricted(feature: TFeature) {
        if (!this.account) {
            return true;
        }

        if (this.isInBookkeeperMode) {
            // restrict features according to the auditee's pricing plan
            const auditeeAccount = this.bookkeeperModeCompany?.account;
            if (!auditeeAccount) {
                return true;
            }
            const auditeeRestrictionMap =
                featureVariationsToFeatureRestrictionMap(
                    auditeeAccount.feature_plan.feature_variations
                );

            return !!auditeeRestrictionMap[feature];
        }

        const featureRestrictions = this.account.featureRestrictions;

        if (!featureRestrictions || !feature) {
            return false;
        }

        return !!featureRestrictions[feature];
    }

    @computed
    get bookkeeperModeCompany() {
        return this.bookkeeperModeCompanyId
            ? this.audited_companies?.find(
                  auditedCompany =>
                      auditedCompany._id === this.bookkeeperModeCompanyId
              )
            : undefined;
    }

    @action
    setAuditedCompanies(auditedCompanies: IAuditeeCompany[]) {
        this.audited_companies = auditedCompanies;
    }

    @action
    setBookkeeperModeCompanyId(companyId: string | undefined) {
        this.bookkeeperModeCompanyId = companyId;
    }

    @action.bound
    addPaymentMethod(paymentMethod: IPaymentMethod) {
        this.payment_methods?.push(new PaymentMethod(paymentMethod));
    }

    @action.bound
    deletePaymentMethod(paymentMethodId: string) {
        const index = this.payment_methods?.findIndex(
            ({ _id }) => _id === paymentMethodId
        );
        if (index !== undefined && index > -1) {
            this.payment_methods.splice(index, 1); // Removes item from observable array
        }
    }

    @action.bound
    setDebitCardId(
        id: string,
        userCtrl?: UserController,
        userService?: UserService
    ) {
        const bankAccountPaymentMethodId =
            this.globalContext.companyStore.currCompany
                ?.bank_account_payment_method_id;

        const bank_account = this.payment_methods?.find(
            pm => pm._id === bankAccountPaymentMethodId
        ) as IStripeBankAccount | undefined;

        this.payment_methods = this.payment_methods?.map(method => {
            if (method._id === bankAccountPaymentMethodId) {
                return {
                    ...method,
                    last_instant_deposit_fail_reason: undefined,
                    debit_card_payment_method_id: id
                };
            }

            return method;
        });

        // Update the user both in angular and react
        // based on this comment: https://github.com/HoneyBook/honeybook-react/commit/ddbe403cb3d3e392cbc7e3b28de2f77391c606d1
        userCtrl?.updateUser(this, {
            payment_methods: this.payment_methods
        });

        try {
            if (bank_account.last_instant_deposit_fail_reason) {
                userService?.clearLastInstantPayoutError(
                    this._id,
                    bankAccountPaymentMethodId
                );
            }
        } catch (e) {}
    }

    @action.bound
    setInstantDepositToBankAccount(
        enabled: boolean,
        userCtrl?: UserController
    ) {
        const bankAccountPaymentMethodId =
            this.globalContext.companyStore.currCompany
                ?.bank_account_payment_method_id;

        this.payment_methods = this.payment_methods?.map(method => {
            if (method._id === bankAccountPaymentMethodId) {
                return {
                    ...method,
                    is_instant_deposit_to_bank_account_enabled: enabled
                };
            }

            return method;
        });

        // Update the user both in angular and react
        // based on this comment: https://github.com/HoneyBook/honeybook-react/commit/ddbe403cb3d3e392cbc7e3b28de2f77391c606d1
        userCtrl?.updateUser(this, {
            payment_methods: this.payment_methods
        });
    }

    @computed
    get paymentMethods() {
        return this.payment_methods;
    }

    @computed
    get subscriptionCoupon() {
        return this.account?.subscription?.coupon;
    }

    isFeatureActive(feature: RolloutFeatureFlags, variation = 'on') {
        return isFeatureActive(this, feature, variation);
    }

    @computed
    get isFlowFlagEnabled() {
        return !!this.account?.enable_flow_flag;
    }

    @computed
    get isBlockFilesFlagEnabled() {
        return !!this.account?.block_files_flag;
    }

    @computed
    get flowsBetaUserType() {
        const hasFlows = this.isFlowFlagEnabled;
        const hasFiles = !this.isBlockFilesFlagEnabled;

        return {
            hasFlows,
            hasFiles,
            hasOnlyFlows: !hasFiles,
            hasOnlyFiles: hasFiles && !hasFlows,
            // TODO: shouldn't we also check that the user doesn't have files? (this is copied from angular)
            hasNewFlowExp: hasFlows
        };
    }

    @computed
    get companyHasSubscription() {
        if (this.company) {
            return this.company.subscribed;
        }

        // the default should be false
        return false;
    }

    @computed
    get isSimplifiedInvoiceEnabled() {
        // Openning the simplified invoice to all members, opening it in split brought up some issues that we need to review
        // will do it in the near future
        return true;
    }

    @computed
    get isGiftCardEnabled() {
        return this.gift_card_feature_allowed;
    }

    isClient() {
        return this.system_user_type === 'client';
    }

    @computed
    get isClientUserType() {
        return !!this.is_client_user_type;
    }

    @computed
    get hasSentFlow() {
        return !!this.first_time_sent_flow_on;
    }

    @computed
    get emailSignature() {
        // email_signatures most time would be an array, but legacy angular shit sometimes return it as an object with a single key of 0.
        return this.email_signatures?.[0];
    }

    @computed
    get userEmail() {
        return this.email;
    }

    @computed
    get fullName() {
        return this.full_name;
    }

    @computed
    get isPro() {
        return this.is_pro;
    }

    @computed
    get isProLogin() {
        return this.is_pro_login;
    }

    @computed
    get isAdminLogin() {
        return this.is_hb_admin;
    }

    @computed
    get isEducator() {
        return this.is_educator;
    }

    @computed
    get hasCalendlyIntegration() {
        return !!this.calendly_url;
    }

    get isTempPassLogin() {
        return this.is_temp_pass_login;
    }

    @computed
    get isExternalUser() {
        return this.isProLogin || this.isAdminLogin || this.isTempPassLogin;
    }

    @computed
    get companyPermission(): IUserPermissions {
        if (this.is_company_owner) {
            return {
                user_id: this._id,
                company_permission: TeamRole.SuperAdmin,
                company_permission_cd: TeamRoleToCd.super_admin
            };
        }

        if (this.company?.user_permissions) {
            const userPermission = this.company.user_permissions.find(
                permission => permission.user_id === this._id
            );
            if (userPermission) {
                return userPermission;
            }
        }

        if (this.user_company_permission) {
            return this.user_company_permission;
        }

        return {
            user_id: this._id,
            company_permission: TeamRole.Basic,
            company_permission_cd: TeamRoleToCd.basic
        };
    }

    /**
     * @deprecated Use companyPermission which has the same logic as Angular
     */
    @computed
    get companyPermissions() {
        if (this.company?.user_permissions) {
            return this.company.user_permissions.find(
                permission => permission.user_id === this._id
            );
        }

        return this.user_company_permission;
    }

    /**
     * @deprecated Should probably use hasCompanySuperAdminPermissions instead
     */
    @computed
    get isSuperAdmin() {
        return (
            this.is_company_owner ||
            this.companyPermissions?.company_permission === 'super_admin'
        );
    }

    hasPermissionsOrHigher = (teamRoleCd: TeamRoleToCd) => {
        return this.companyPermission?.company_permission_cd >= teamRoleCd;
    };

    @computed
    get hasCompanySuperAdminPermissions() {
        return this.hasPermissionsOrHigher(TeamRoleToCd.super_admin);
    }

    @computed
    get hasCompanyAdminPermissions() {
        return this.hasPermissionsOrHigher(TeamRoleToCd.admin);
    }

    @computed
    get hasCompanyModeratorPermissions() {
        return this.hasPermissionsOrHigher(TeamRoleToCd.moderator);
    }

    @computed
    get canEditCompanyPreferences() {
        return this.hasPermissionsOrHigher(
            TeamRoleToCd[
                this.company?.company_setting_permissions || TeamRole.Basic
            ]
        );
    }

    @computed
    get hasTrialExpiredDueToNoPayment() {
        return !!this.account?.trial?.ended && !this.isSubscribed;
    }

    @computed
    get daysSinceTrialEndDate() {
        const trialData = this.account?.trial;

        if (!trialData) {
            return;
        }

        const trialEndDate = dayjs(trialData.start_date).add(
            trialData.duration,
            'day'
        );
        return dayjs().diff(trialEndDate, 'day');
    }

    @computed
    get isInIntentTestGroup() {
        const { intent_infra } = this.ab_tests || {};

        return (
            (this.account?.trial?.start_date &&
                dayjs(this.account.trial.start_date).isAfter('2023-09-20')) ||
            intent_infra === 'intent_test'
        );
    }

    isInvoiceIntentBehavior() {
        const userIntent = this.user_intent;
        return (
            this.isInIntentTestGroup &&
            (!userIntent ||
                userIntent === UserIntentTypes.invoices ||
                userIntent === UserIntentTypes.templates)
        );
    }

    isInSpecificIntentGroup(intent: UserIntentType) {
        return this.isInIntentTestGroup && this.user_intent === intent;
    }

    @computed
    get isMSPEligable() {
        return (
            this.account?.trial?.start_date &&
            dayjs(this.account.trial.start_date).isAfter(MSP_FEATURE_DATE)
        );
    }

    @computed
    get isConsultantAdjustmentsFeature() {
        // https://console.statsig.com/5XY7V8GlxGy1kEv0oEI7pk/gates/consultant_vertical
        return (
            this.account?.trial?.start_date &&
            dayjs(this.account.trial.start_date).isAfter('2023-07-09') &&
            this.globalContext.companyStore.currCompany?.isConsultant
        );
    }

    @computed
    get myBankAccount() {
        if (!this.paymentMethods) {
            return null;
        }

        if (
            this.globalContext.companyStore.currCompany.bank_account_user_id ===
            this._id
        ) {
            const paymentMethod = this.paymentMethods.find(
                method =>
                    method._id ===
                    this.globalContext.companyStore.currCompany
                        .bank_account_payment_method_id
            ) as IStripeBankAccount;

            return paymentMethod;
        }
    }

    @computed
    get hasOldBankAccount() {
        const bankAccount = this.myBankAccount;
        const isCanadian = this.company?.country?.toLowerCase() === 'ca';

        return (
            isCanadian &&
            !!bankAccount &&
            !bankAccount.company_structure &&
            !!bankAccount.global_fields_to_fix &&
            bankAccount.global_fields_to_fix?.includes('directors_provided')
        );
    }

    @computed
    get hasOldAccountNoStructureDirectorsUnknown() {
        const bankAccount = this.myBankAccount;
        const isCanadian = this.company?.country?.toLowerCase() === 'ca';

        return isCanadian && !!bankAccount && !bankAccount.company_structure;
    }

    @computed
    get myDebitCard() {
        const bank_account = this.myBankAccount;

        if (!bank_account?.debit_card_payment_method_id) {
            return null;
        }

        return this.paymentMethods.find(
            method =>
                bank_account.debit_card_payment_method_id === method._id &&
                method.card_role.includes('instant_payout')
        );
    }

    @computed
    get isConnectedToFacebook() {
        return this.social_network_accounts?.some(
            social => social.social_network_type === 'facebook'
        );
    }

    @computed
    get isNylasCalendarIntegrationEnabled() {
        return !!this.nylas_calendar_feature_allowed;
    }

    isCompanyOwner() {
        // TODO Alon - Why not use this.is_company_owner ??
        // Dvir: alon why asking questions instead of doing actions?
        // Erez: dvir, why not removing todos if done?
        // Alon: Erez, why are you friends with Dvir?
        // Boaz: Nothing here in 2021 yet, dibs
        // Dvir: we missed 2022! here's 2023 (btw my mom's birthday)
        // Uri: It's 2024 and this method has now moved to React!
        if (this.is_company_owner) {
            return true;
        }

        if (this._id && this.company && this.company.owner) {
            return this._id === this.company.owner._id;
        }

        return false;
    }

    @computed
    get isLeadFormsUser() {
        if (!this.isVendor()) {
            return false;
        }

        return this.flowsBetaUserType.hasFlows;
    }

    @computed
    get isVendorAsClient() {
        return this.workspace_user_data?.workspace_role === WorkspaceRoles.HOST;
    }

    isAccountOwner() {
        return !!this.is_account_owner;
    }

    companyHasFreeSubscription() {
        if (this.company) {
            return this.company.subscribed_for_free;
        }

        return false;
    }

    shouldShowMembershipToUser() {
        return (
            this.isAccountOwner() &&
            this.company.shouldShowMembership() &&
            !this.companyHasFreeSubscription()
        );
    }

    shouldShowClientPortal() {
        return (
            this.hasCompanySuperAdminPermissions &&
            (this.companyHasAnySubscription() ||
                !!this.company.company_url_prefixes?.length)
        );
    }

    companyHasAnySubscription() {
        return this.companyHasSubscription || this.companyHasFreeSubscription();
    }

    @action.bound
    setDefaultAutoExpiration(value: TDefaultAutoExpirationOption | null) {
        this.default_auto_expiration_period = value;
    }

    @action.bound
    setHasCanvaIntegration(value: boolean) {
        this.has_canva_integration = value;
    }

    isOtamMigrated() {
        return (
            this.flowsBetaUserType.hasFiles && this.flowsBetaUserType.hasFlows
        );
    }

    getAbTestVariation<T extends TABTestKeys>(
        abTestName: T
    ): TABTestValue<T> | undefined {
        return this.ab_tests?.[abTestName] as TABTestValue<T>;
    }

    toUserResponse() {
        return JSON.parse(tryParseToJsonString(this).json) as IUserResponse;
    }

    @action.bound
    setFinBotAuthToken(token: string) {
        this.finBotAuthToken = token;
    }

    isSubscribedAtLeastXDays(days: number): boolean {
        if (!this.account?.subscription?.start_date) {
            return false;
        }

        const dateXDaysAgo = dayjs().subtract(days, 'days');
        const subscriptionStartDate = dayjs(
            this.account.subscription.start_date
        );

        return subscriptionStartDate.isBefore(dateXDaysAgo);
    }

    @computed
    get isNetworkPretrialer() {
        return (
            this.attribution?.user_source_primary === 'network' &&
            this.shouldStartTrial
        );
    }

    @computed
    get isInTrialAndNotSubscribed() {
        // If no company exists, user is considered in trial
        if (!this.company) {
            return true;
        }

        // If company is subscribed, user is not in trial
        if (this.company.subscribed) {
            return false;
        }

        const trial = this.getTrial();
        // No trial means not in trial
        if (!trial) {
            return false;
        }

        // User is in trial if trial hasn't ended
        return !trial.ended;
    }

    @action.bound
    setUserSetupSequence<T extends IOnboardingStep = IOnboardingStep>(
        userSetupSequence: OnboardingSequence<T>
    ) {
        this.userSetupSequence = userSetupSequence;
    }

    @action.bound
    setCompanies(companies: MinimalCompany[]) {
        this.companies = companies;
    }
}
